import React, {
  useEffect,
  createContext,
  useState,
  useCallback,
  useMemo
} from 'react';
import PropTypes from 'prop-types';

import { gtagEvent } from 'src/utils/gtag';
import useRestApiClient from 'src/hooks/useRestApiClient';
import { filterListBy } from 'src/utils/helpers/filter';

const initialState = {
  myTemplates: [],
  filteredMyTemplates: [],
  isLoading: false,
  isPreloaded: false,
  filterValues: { category: '', industry: '', search_title: '' },
  setFilterValues: () => Promise.resolve(),
  updateTemplateLocal: () => {}
};

const MyTemplatesContext = createContext(initialState);

export const MyTemplatesProvider = (props) => {
  const { children } = props;
  const [filterValues, setFilterValues] = useState(initialState.filterValues);
  const [loading, setLoading] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [myTemplates, setMyTemplates] = useState(initialState.myTemplates);
  const [isPreloaded, setPreloaded] = useState(false);
  const [filteredMyTemplates, setFilteredMyTemplates] = useState(
    initialState.filteredMyTemplates
  );
  const client = useRestApiClient();

  const deleteMyTemplate = async (templateId) => {
    setRemoving(true);
    await client.delete(`/templates/${templateId}`);
    setMyTemplates((prev) => {
      const filtered = prev.filter((row) => row.id !== templateId);
      return [...filtered];
    });
    setRemoving(false);
  };

  const loadMyTemplates = async (force = false) => {
    if (myTemplates?.length > 0 && !force) return;
    if (loading) return;

    try {
      setLoading(true);
      const {
        data: { message }
      } = await client.get('/collections/templates');
      setMyTemplates(message);
      setPreloaded(true);
      // setFilteredMyTemplates(message);
    } catch (error) {
      setMyTemplates([]);
      if (error?.response?.status === 404) {
        setPreloaded(true);
      }
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const saveTemplate = async (payload) => {
    try {
      setSubmitting(true);
      const {
        data: { message: newTemplate }
      } = await client.post('/templates', payload);

      setMyTemplates((currentList) => [...currentList, newTemplate]);
      setPreloaded(true);
      setSubmitting(false);
    } catch (error) {
      setSubmitting(false);
      throw error;
    }
  };

  const updateTemplate = async (templateId, payload) => {
    let result = {};
    try {
      setSubmitting(true);
      const {
        data: { message }
      } = await client.patch(`/templates/${templateId}`, payload);

      if (message.pending_approval) {
        delete payload.private;
      }

      result = message;
    } catch (error) {
      setSubmitting(false);
      throw error;
    }
    setSubmitting(false);

    return result;
  };

  const updateTemplateLocal = useCallback(
    (templateId, data) => {
      setMyTemplates((currentList) => {
        const newList = currentList.map((template) => {
          return template.id === templateId
            ? { ...template, ...data }
            : template;
        });
        return [...newList];
      });
    },
    [myTemplates]
  );

  useEffect(() => {
    let templates = myTemplates;
    const { category, industry, search_title: searchTitle } = filterValues;

    templates = filterListBy(templates, 'category', category);
    templates = filterListBy(templates, 'industry', industry);

    if (searchTitle) {
      gtagEvent('search', {
        search_term: searchTitle
      });

      templates = templates.filter(
        ({ description, title }) =>
          (description &&
            description.toLowerCase().indexOf(searchTitle.toLowerCase()) >
              -1) ||
          (title && title.toLowerCase().indexOf(searchTitle.toLowerCase()) > -1)
      );
    }

    setFilteredMyTemplates([...templates]);
  }, [myTemplates, filterValues]);

  const values = useMemo(
    () => ({
      loading,
      isPreloaded,
      removing,
      submitting,
      filterValues,
      myTemplates,
      filteredMyTemplates,
      updateTemplateLocal,
      setFilterValues,
      loadMyTemplates,
      saveTemplate,
      updateTemplate,
      deleteMyTemplate
    }),
    [
      loading,
      isPreloaded,
      removing,
      submitting,
      filterValues,
      myTemplates,
      filteredMyTemplates,
      updateTemplateLocal,
      updateTemplate
    ]
  );

  return (
    <MyTemplatesContext.Provider value={values} {...props}>
      {children}
    </MyTemplatesContext.Provider>
  );
};

MyTemplatesProvider.propTypes = {
  children: PropTypes.node.isRequired
};

export default MyTemplatesContext;
